'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Button from '@/app/(football)/[team]/_components/common/Button';
import StickerCard from '@/app/(football)/[team]/_components/common/StickerCard';
import useStickerCard from '@/app/(football)/[team]/_components/common/StickerCard/_hooks/useStickerCard';
import type { ExternalLinks, StickerCategories, TeamSlug } from '@/app/(football)/[team]/_context/types';
import { trackEvent } from '@/libs/analytics/events';
import type { PackName } from '@/store/server/common/digital-card/_constant';
import {
  useGetAllCardCollectionWithInitQuery,
  useGetMyCardCollectionWithInitQuery,
} from '@/store/server/common/digital-card/digital-card.query';
import { useGetRepresentCardWithInitQuery } from '@/store/server/common/football/football.query';
import type { OriginalsName } from '@/store/server/common/originals/originals.dto';

import BottomSheet from './BottomSheet';
import Header from './Header';

export type Step = 'select-card' | 'select-sticker';

const CANVAS_SIZE = { width: 260, height: 400 };

export const SET_DEFAULT_CARD_MODAL_KEY = 'set-default-card-modal';

interface SetDefaultCardModalProps {
  originalsName: OriginalsName;
  packName: string;
  teamSlug: TeamSlug;
  externalLinks: ExternalLinks | undefined;
  stickerCategories: StickerCategories;
  history: Record<number, { packName: PackName; originalsName: OriginalsName }> | undefined;
}

export default function SetDefaultCardModal({ originalsName, packName, teamSlug, externalLinks, stickerCategories, history }: SetDefaultCardModalProps) {

  const router = useRouter();
  const [step, setStep] = useState<Step>('select-card');

  const {
    stickerCard,
    selectedStickerId,
    initStickerCard,
    selectStickerCard,
    addSticker,
    removeSticker,
    updateSticker,
    selectSticker,
  } = useStickerCard({ canvasSize: CANVAS_SIZE });

  const { data: representCardData, isLoading: isLoadingRepresentCard } = useGetRepresentCardWithInitQuery({
    originalsName,
  });
  const { data: allCardsData, isLoading: isLoadingAllCards } = useGetAllCardCollectionWithInitQuery({
    originalsName,
    packName: packName as PackName,
  });
  const { data: myCardsData, isLoading: isLoadingMyCards } = useGetMyCardCollectionWithInitQuery({
    originalsName,
    packName: packName as PackName,
  });

  const hasHistory = history && Object.keys(history).length > 0;
  const historyEntry = hasHistory ? Object.values(history)[0] : undefined;
  const { data: historyMyCardsData, isLoading: isLoadingHistoryMyCards } = useGetMyCardCollectionWithInitQuery({
    originalsName: (historyEntry?.originalsName ?? originalsName) as OriginalsName,
    packName: (historyEntry?.packName ?? packName) as PackName,
  });

  const isLoading = isLoadingRepresentCard || isLoadingAllCards || isLoadingMyCards || (hasHistory && isLoadingHistoryMyCards);

  const myCardList = useMemo(() => {
    if (!allCardsData || !myCardsData) return [];
    return allCardsData.cardList.filter((card) =>
      myCardsData.cardList.some((myCard) => myCard.metadataIdx === card.metadataIdx),
    );
  }, [allCardsData, myCardsData]);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (representCardData && !initializedRef.current) {
      initializedRef.current = true;
      initStickerCard({
        metadataIdx: representCardData.metadataIdx,
        cardName: representCardData.cardName,
        image: representCardData.image,
        width: representCardData.width,
        height: representCardData.height,
        stickers: representCardData.stickers,
      });
    }
  }, [representCardData, initStickerCard]);

  // 대표카드가 선택되지 않았으면 첫번째 카드를 대표카드로 선택
  useEffect(() => {
    if (myCardList.length > 0 && !representCardData && !initializedRef.current) {
      const myFirstCard = myCardList[0];
      selectStickerCard(myFirstCard.metadataIdx, myFirstCard.cardName, myFirstCard.image);
    }
  }, [myCardList, representCardData, selectStickerCard]);

  // Legacy scroll lock: save scrollY, fix body, restore on unmount
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  const handleSelectCard = useCallback(
    (metadataIdx: number, cardName: string, image: string) => {
      selectStickerCard(metadataIdx, cardName, image);
    },
    [selectStickerCard],
  );

  const handleAddSticker = useCallback(
    (idx: string, src: string) => {
      addSticker(idx, src);
    },
    [addSticker],
  );

  const handleSelectSticker = useCallback(
    (idx: string) => {
      selectSticker(idx);
    },
    [selectSticker],
  );

  const handleOpenBuyCard = () => {
    const shopUrl = externalLinks?.shopUrl;
    if (!shopUrl) return;
    // shopUrl 이 빈 값일 땐 CTA 가 비렌더이므로 이곳 도달 자체가 없어야 하지만,
    // 방어적으로 no-op 처리 + 이벤트도 유효한 shopUrl 일 때만 쏘도록 가드.
    trackEvent({ name: 'buy_card_external', params: { cta_position: 'main_set_rep_card' } });
    // `/` prefix → 내부 라우트(SPA 이동), 그 외 → 외부 링크 새 탭.
    if (shopUrl.startsWith('/')) {
      router.push(shopUrl);
    } else {
      window.open(shopUrl, '_blank');
    }
  };

  const isSelectStickerStep = step === 'select-sticker';

  const currentPackHasCards = myCardsData?.cardList?.some((c) => c.amount > 0) ?? false;
  const historyPackHasCards = hasHistory && (historyMyCardsData?.cardList?.some((c) => c.amount > 0) ?? false);
  const hasNoCards = !isLoading && !currentPackHasCards && !historyPackHasCards;

  const initialPack = useMemo(() => {
    if (currentPackHasCards) return { packName, originalsName };
    if (historyPackHasCards && historyEntry) return { packName: historyEntry.packName, originalsName: historyEntry.originalsName };
    return { packName, originalsName };
  }, [currentPackHasCards, historyPackHasCards, packName, originalsName, historyEntry]);

  return (
    <div
      className={`relative flex h-dvh w-screen max-w-[480px] flex-col ${isLoading || hasNoCards ? 'bg-black' : 'bg-card-deco-modal'}`}
    >
      <Header stickerCard={stickerCard} originalsName={originalsName} isSelectStickerStep={isSelectStickerStep} />

      {/* Canvas Area (Body) */}
      <div className="flex items-center justify-center">
        {isLoading ? (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="team-spinner h-8 w-8 rounded-full border-2" />
          </div>
        ) : hasNoCards ? (
          <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-4">
            <Image
              src={`https://cdn.kottonseed.io/assets/football/2026/${teamSlug}/icons/icon_illust_card.webp`}
              alt="empty-result"
              width={48}
              height={48}
            />
            <p className="typo-body-6 text-white">아직 보유 카드가 없어요.</p>
            {externalLinks?.shopUrl && (
              <Button variant="secondary" onClick={handleOpenBuyCard} className="mt-2 w-[134px]">
                카드 구매하러가기
              </Button>
            )}
          </div>
        ) : stickerCard || hasHistory ? (
          <>
            {stickerCard && (
              <div className="pt-6">
                {isSelectStickerStep ? (
                  <StickerCard
                    mode="edit"
                    image={stickerCard.image}
                    width={stickerCard.width}
                    height={stickerCard.height}
                    stickers={stickerCard.stickers}
                    selectedStickerId={selectedStickerId}
                    selectSticker={selectSticker}
                    updateSticker={updateSticker}
                    removeSticker={removeSticker}
                  />
                ) : (
                  <StickerCard
                    mode="view"
                    image={stickerCard.image}
                    width={stickerCard.width}
                    height={stickerCard.height}
                    stickers={stickerCard.stickers}
                    containerSize={CANVAS_SIZE}
                  />
                )}
              </div>
            )}
            <BottomSheet
              step={step}
              setStep={setStep}
              packName={packName}
              originalsName={originalsName}
              initialPackName={initialPack.packName}
              initialOriginalsName={initialPack.originalsName}
              stickerCard={stickerCard}
              onSelectCard={handleSelectCard}
              onAddSticker={handleAddSticker}
              onSelectSticker={handleSelectSticker}
              teamSlug={teamSlug}
              stickerCategories={stickerCategories}
              history={history}
            />
          </>
        ) : (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="team-spinner h-8 w-8 rounded-full border-2" />
          </div>
        )}
      </div>
    </div>
  );
}
